/**
 * Shared Google Places (New) Nearby Search request-shape contract (COR-61).
 *
 * `apps/web` (GettingThereCard) and `data-pipeline` (transit derivation) must
 * POST the same URL, FieldMask, and body keys so a FieldMask bump in one
 * caller cannot silently bill the other at a higher SKU. HTTP auth, timeout,
 * and fetch stay in each caller.
 *
 * FieldMask is Essentials only (`id`, `displayName`, `location`, `types`).
 * Do not add Enterprise or Atmosphere amenity SKUs — Google bills the
 * request at the highest tier touched by the mask.
 */
import { z } from 'zod';

export const NEARBY_SEARCH_URL = 'https://places.googleapis.com/v1/places:searchNearby';

/**
 * Essentials FieldMask paths. Joined for the `X-Goog-FieldMask` header.
 * Keep this list exhaustive for the contract test — adding a path is a
 * billing decision, not a drive-by.
 */
export const NEARBY_SEARCH_FIELD_MASK_FIELDS = [
  'places.id',
  'places.displayName',
  'places.location',
  'places.types',
] as const;

export const NEARBY_SEARCH_FIELD_MASK = NEARBY_SEARCH_FIELD_MASK_FIELDS.join(',');

/**
 * Table A transit types used by GettingThereCard and COR-61 derivation.
 * Nearby Search `includedTypes` (request body), not FieldMask.
 */
export const NEARBY_SEARCH_TRANSIT_TYPES = [
  'transit_station',
  'subway_station',
  'light_rail_station',
  'train_station',
  'bus_station',
] as const;

/** Table A parking types used by GettingThereCard (not the transit lane). */
export const NEARBY_SEARCH_PARKING_TYPES = [
  'parking',
  'parking_garage',
  'parking_lot',
] as const;

export type NearbyPlaceType =
  | (typeof NEARBY_SEARCH_TRANSIT_TYPES)[number]
  | (typeof NEARBY_SEARCH_PARKING_TYPES)[number];

/**
 * Default search radius in meters. Matches GettingThereCard
 * (`SEARCH_RADIUS_M` / FRE-1292): a realistic walk-to-transit envelope
 * without pulling highway park-and-rides that are not "this venue is
 * transit-accessible."
 */
export const NEARBY_SEARCH_DEFAULT_RADIUS_METERS = 800;

export const NEARBY_SEARCH_MAX_RADIUS_METERS = 50_000;
export const NEARBY_SEARCH_MIN_MAX_RESULT_COUNT = 1;
export const NEARBY_SEARCH_MAX_MAX_RESULT_COUNT = 20;
export const NEARBY_SEARCH_DEFAULT_MAX_RESULT_COUNT = 3;

export type NearbySearchRankPreference = 'DISTANCE' | 'POPULARITY';

export interface NearbySearchRequestInput {
  latitude: number;
  longitude: number;
  includedTypes: ReadonlyArray<string>;
  radiusMeters?: number;
  maxResultCount?: number;
  languageCode?: string;
  rankPreference?: NearbySearchRankPreference;
}

export interface NearbySearchPlace {
  placeId: string;
  displayName: string;
  latitude: number;
  longitude: number;
  types: readonly string[];
}

export const nearbySearchResponseSchema = z.object({
  places: z
    .array(
      z.object({
        id: z.string().optional(),
        displayName: z.object({ text: z.string().optional() }).optional(),
        location: z
          .object({
            latitude: z.number().optional(),
            longitude: z.number().optional(),
          })
          .optional(),
        types: z.array(z.string()).optional(),
      }),
    )
    .optional(),
});

export type NearbySearchResponse = z.infer<typeof nearbySearchResponseSchema>;

function clampRadius(meters: number): number {
  if (!Number.isFinite(meters) || meters <= 0) {
    return NEARBY_SEARCH_DEFAULT_RADIUS_METERS;
  }
  return Math.min(NEARBY_SEARCH_MAX_RADIUS_METERS, meters);
}

function clampMaxResultCount(count: number): number {
  if (!Number.isInteger(count)) {
    return NEARBY_SEARCH_DEFAULT_MAX_RESULT_COUNT;
  }
  return Math.min(
    NEARBY_SEARCH_MAX_MAX_RESULT_COUNT,
    Math.max(NEARBY_SEARCH_MIN_MAX_RESULT_COUNT, count),
  );
}

/** JSON body for `POST places:searchNearby`. */
export function buildNearbySearchRequestBody(
  input: NearbySearchRequestInput,
): Record<string, unknown> {
  return {
    includedTypes: [...input.includedTypes],
    maxResultCount: clampMaxResultCount(
      input.maxResultCount ?? NEARBY_SEARCH_DEFAULT_MAX_RESULT_COUNT,
    ),
    languageCode: input.languageCode ?? 'en',
    rankPreference: input.rankPreference ?? 'DISTANCE',
    locationRestriction: {
      circle: {
        center: { latitude: input.latitude, longitude: input.longitude },
        radius: clampRadius(input.radiusMeters ?? NEARBY_SEARCH_DEFAULT_RADIUS_METERS),
      },
    },
  };
}

/** Map a parsed Nearby Search payload onto usable place rows; skip incomplete ones. */
export function mapNearbySearchPlaces(payload: NearbySearchResponse): NearbySearchPlace[] {
  const results: NearbySearchPlace[] = [];
  for (const place of payload.places ?? []) {
    const placeId = place.id?.trim();
    const displayName = place.displayName?.text?.trim();
    const latitude = place.location?.latitude;
    const longitude = place.location?.longitude;
    if (
      !placeId ||
      !displayName ||
      latitude === undefined ||
      longitude === undefined ||
      !Number.isFinite(latitude) ||
      !Number.isFinite(longitude)
    ) {
      continue;
    }
    results.push({
      placeId,
      displayName,
      latitude,
      longitude,
      types: place.types ?? [],
    });
  }
  return results;
}
